/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useMemo } from "react";

import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import TextArea from "@/components/TextArea";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import {
  aiParamsStore,
  parseMetaToParams,
  setImportMetaParamStr,
  AIParamsMeta,
  AIParamsType,
  updateParamsDataValue,
  updateVariations,
  MetaBizTypeMap,
  updateCustomEmbeddings,
} from "@/modules/editor/stores/aiParams";
import { MetaType } from "@/types/meta";
import { Actions } from "@/utils/api";
import { Editor_Words } from "@/utils/lang/editor";
import { toast, useOwnToast } from "@/utils/misc";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { langStore, translate } from "@noli/business";
import { observer } from "mobx-react-lite";
import { SamplerSelector } from "../../../components/SamplerSelector";
import { getRandomHash } from "@noli/core";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { ModelSwitch } from "@/modules/editor/components/common/ModelSwitch";
import {
  AI_PARAMS_TYPES,
  ModelTypes,
  NoliboxModelTypeEnum,
  NoliboxModelTypes,
  NOLIBOX_MODEL_TYPES,
} from "@/modules/editor/constant";
import { AI_Params_Words } from "@/utils/lang/aiParams";
import { NSlider } from "@/components/NSlider";
import { CircularSwitch } from "@/modules/editor/components/common/CircularSwitch";
import { InputField } from "../../../components/ParamInputField";
import { ParamsTypeSwitch } from "./components/ParamsTypeSwitch";
import { cloneDeep } from "lodash";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { SmallModelsSelector } from "@/modules/editor/components/SmallModelsSelector";
import { isAuthUser, isVip } from "@/utils/status";
import { HighResSwitch } from "@/modules/editor/components/common/HighResSwitch";
import { AI_MAX_WH, HIGH_RES_ADVANCE_MODE_MAX_WH } from "@/utils/constants";

type RenderConfigType =
  | "modelType"
  | "prompt"
  | "negativePrompt"
  | "url"
  | "fidelity"
  | "maskUrl"
  | "circular"
  | "num_steps"
  | "guidance_scale"
  | "sampler"
  | "seed"
  | "w"
  | "h"
  | "useRefine"
  | "variations"
  | "smallModels"
  | "highRes";

const IParams2Img: React.FC = () => {
  const lang = langStore.tgt;
  const t = useOwnToast();
  const { data, type, model } = aiParamsStore;

  // const importMeta = useMemo(() => {
  //   try {
  //     const meta = JSON.parse(aiParamsStore.importMetaParamStr) as AIParamsMeta;
  //     return meta;
  //   } catch (err) {
  //     console.warn("parse json error", err);
  //   }
  // }, [aiParamsStore.importMetaParamStr]);

  // const importMetaConvertable = useMemo(
  //   () => importMeta?.type && !AI_PARAMS_TYPES.includes(importMeta.type),
  //   [importMeta?.type],
  // );
  // const importMetaWH = useMemo(() => {
  //   return {
  //     // @ts-ignore
  //     w: importMeta?.data?.wh?.w ?? importMeta?.data?.w ?? 0,
  //     // @ts-ignore
  //     h: importMeta?.data?.wh?.h ?? importMeta?.data?.h ?? 0,
  //   };
  // }, [importMeta]);
  console.log("data: ", data);
  const renderConfigMap: Record<AIParamsType, Partial<Record<RenderConfigType, boolean>>> = useMemo(
    () => ({
      txt2img: {
        modelType: true,
        prompt: true,
        negativePrompt: true,
        circular: true,
        num_steps: true,
        guidance_scale: true,
        sampler: true,
        seed: true,
        w: true,
        h: true,
        smallModels: true,
        highRes: isAuthUser(),
      },
      "txt2img.inpainting": {
        url: true,
        maskUrl: true,
        prompt: true,
      },
      "txt2img.variation": {
        modelType: true,
        prompt: true,
        negativePrompt: true,
        circular: true,
        num_steps: true,
        guidance_scale: true,
        sampler: true,
        seed: true,
        w: true,
        h: true,
        variations: true,
      },
      img2img: {
        modelType: true,
        prompt: true,
        negativePrompt: true,
        circular: true,
        num_steps: true,
        guidance_scale: true,
        sampler: true,
        seed: true,
        url: true,
        fidelity: true,
        smallModels: true,
      },
      "img2img.variation": {
        modelType: true,
        prompt: true,
        negativePrompt: true,
        circular: true,
        num_steps: true,
        guidance_scale: true,
        sampler: true,
        seed: true,
        url: true,
        fidelity: true,
        variations: true,
      },
      sr: {
        modelType: true,
        url: true,
      },
      landscape: {
        modelType: true,
        url: true,
      },
      inpainting: {
        url: true,
        maskUrl: true,
        useRefine: true,
      },
      "txt2img.outpainting": {
        url: true,
        prompt: true,
        negativePrompt: true,
      },
    }),
    [isAuthUser(), isVip()],
  );
  // const { cost, affordable } = useGenerateCost({
  //   generateCount: 1,
  //   calcPriceProps: type ? GeneratePriceRules[MetaBizTypeMap[type]] : undefined,
  //   size: { w: data?.w ?? 512, h: data?.h ?? 512 },
  // });

  function handleGenerate() {
    try {
      if (!type) {
        throw Error();
      }

      const metaData = cloneDeep(data);
      const meta = {
        type,
        data: metaData,
      } as MetaType;
      if ("is_anime" in meta.data) {
        meta.data.is_anime = model === "anime";
      }

      handleImport(meta);
    } catch {
      toast(t, "error", translate(Editor_Words["import-error-message"], lang));
    }
  }

  async function handleImport(meta: MetaType): Promise<void> {
    try {
      await Actions.importMeta({
        t,
        lang,
        type: meta.type,
        meta,
        setupInfo: { checkBusy: true, busyAliases: [".import.meta."] },
        opt: { freezePrompt: true },
      } as any);
    } catch {
      toast(t, "error", translate(Editor_Words["import-error-message"], lang));
    }
  }

  function handleConvert() {
    try {
      const meta = JSON.parse(aiParamsStore.importMetaParamStr) as AIParamsMeta;
      if (!AI_PARAMS_TYPES.includes(meta.type)) {
        toast(
          t,
          "info",
          translate(AI_Params_Words.typeNotSupportMessage, lang).replace("$1", meta.type),
        );
        handleImport(meta);
        return;
      }
      parseMetaToParams(meta);
    } catch {
      toast(t, "error", translate(AI_Params_Words.parseParamsErrorMessage, lang));
    }
  }

  const renderConfig = useMemo(() => {
    if (type) {
      return renderConfigMap[type] ?? {};
    }
    return {};
  }, [type, isVip(), isAuthUser()]);

  const { w, h } = useMemo(() => {
    const result = {
      w: data.w,
      h: data.h,
    };

    if (data.highres_info) {
      result.w = result.w * (data.highres_info.upscale_factor ?? 2);
      result.h = result.h * (data.highres_info.upscale_factor ?? 2);
    }
    return result;
  }, [data.w, data.h, data.highres_info]);

  return (
    <SubWithTitle title={translate(Editor_Words["import-title"], lang)} pb="24px">
      <Text fontSize="14px" color="#E5E5E5" mb="8px" mt="16px">
        {translate(Editor_Words["params2txt-input-title"], lang)}:
      </Text>
      <TextArea
        w="100%"
        lang={lang}
        textAreaH={160}
        minRows={5}
        maxRows={7}
        placeholderWord={Editor_Words["import-placeholder"]}
        text={aiParamsStore.importMetaParamStr}
        setText={setImportMetaParamStr}
        mt="0"
      />
      <InfoText mt="8px">{translate(Editor_Words["params2txt-input-desc"], lang)}</InfoText>
      <Button
        w="100%"
        mt="12px"
        mb="16px"
        onClick={handleConvert}
        disabled={!aiParamsStore.importMetaParamStr}
      >
        导入
      </Button>

      <Box>
        {type && (
          <ParamsTypeSwitch
            direction="column"
            value={type}
            onChange={(type) => aiParamsStore.updateProperty("type", type)}
          />
        )}
        {renderConfig.modelType && (
          <ModelSwitch
            mt="16px"
            direction="column"
            enableModelTypes={NOLIBOX_MODEL_TYPES}
            value={model}
            onChange={(value) => aiParamsStore.updateProperty("model", value as NoliboxModelTypes)}
          />
        )}
        {renderConfig.url && (
          <>
            <LabelText mt="12px">{translate(AI_Params_Words.originImageCaption, lang)}：</LabelText>
            <ImageWithPlaceHolder mt="8px" url={data.url} placeholderText="" />
          </>
        )}
        {renderConfig.maskUrl && (
          <>
            <LabelText mt="12px">{translate(AI_Params_Words.maskUrlCaption, lang)}：</LabelText>
            <ImageWithPlaceHolder mt="8px" url={data.mask_url} placeholderText="" />
          </>
        )}
        {renderConfig.prompt && (
          <>
            <LabelText mt="12px">{translate(AI_Params_Words["prompt-title"], lang)}：</LabelText>
            <TextArea
              w="100%"
              mt="8px"
              lang={lang}
              textAreaH={300}
              minRows={4}
              maxRows={4}
              placeholderWord={AI_Params_Words["prompt-placeholder"]}
              text={aiParamsStore.data.text ?? ""}
              setText={(text) => {
                updateParamsDataValue("text", text);
                if (data.promptInfo) {
                  updateParamsDataValue("promptInfo", {
                    ...data.promptInfo,
                    original: text,
                  });
                }
              }}
            />
          </>
        )}
        {renderConfig.negativePrompt && (
          <>
            <LabelText mt="12px">
              {translate(AI_Params_Words["negative-prompt-title"], lang)}：
            </LabelText>
            <TextArea
              w="100%"
              mt="8px"
              lang={lang}
              textAreaH={300}
              minRows={4}
              maxRows={4}
              placeholderWord={AI_Params_Words["negative-prompt-placeholder"]}
              text={aiParamsStore.data.negative_prompt ?? ""}
              setText={(text) => {
                updateParamsDataValue("negative_prompt", text);
                if (data.promptInfo) {
                  updateParamsDataValue("promptInfo", {
                    ...data.promptInfo,
                    originalNegative: text,
                  });
                }
              }}
            />
          </>
        )}

        {renderConfig.sampler && (
          <SamplerSelector
            value={data.sampler}
            onChange={(sampler) => {
              updateParamsDataValue("sampler", sampler);
            }}
          />
        )}
        {renderConfig.smallModels && (
          <SmallModelsSelector
            type={NoliboxModelTypeEnum[model]}
            value={data.custom_embeddings ?? []}
            onChange={updateCustomEmbeddings}
          />
        )}
        {renderConfig.fidelity && (
          <NSlider
            mt="12px"
            value={data.fidelity}
            defaultValue={data.fidelity}
            max={1}
            min={0}
            step={0.01}
            label={translate(AI_Params_Words["fidelity-caption"], lang)}
            onChange={(fidelity) => {
              updateParamsDataValue("fidelity", fidelity);
            }}
          />
        )}

        {renderConfig.w && (
          <NSlider
            mt="12px"
            value={data.w}
            max={renderConfig.highRes ? HIGH_RES_ADVANCE_MODE_MAX_WH : AI_MAX_WH}
            min={64}
            step={64}
            label={translate(AI_Params_Words["w-caption"], lang)}
            onChange={(w) => {
              updateParamsDataValue("w", w);
            }}
          />
        )}
        {renderConfig.h && (
          <NSlider
            value={data.h}
            max={renderConfig.highRes ? HIGH_RES_ADVANCE_MODE_MAX_WH : AI_MAX_WH}
            min={64}
            step={64}
            label={translate(AI_Params_Words["h-caption"], lang)}
            onChange={(h) => {
              updateParamsDataValue("h", h);
            }}
          />
        )}
        {renderConfig.num_steps && (
          <NSlider
            value={data.num_steps}
            max={100}
            min={5}
            step={1}
            label={translate(AI_Params_Words["num-steps-caption"], lang)}
            onChange={(num_steps) => {
              updateParamsDataValue("num_steps", num_steps);
            }}
          />
        )}
        {renderConfig.guidance_scale && (
          <NSlider
            value={data.guidance_scale}
            max={30}
            min={-20}
            step={0.5}
            label={translate(AI_Params_Words["guidance-scale-caption"], lang)}
            onChange={(guidance_scale) => {
              updateParamsDataValue("guidance_scale", guidance_scale);
            }}
          />
        )}
        {renderConfig.variations && (
          <>
            <LabelText mt="12px" mb="8px">
              {translate(AI_Params_Words["variations-title"], lang)}：
            </LabelText>
            {data.variations?.map((variation, index) => {
              return (
                <Flex
                  key={index}
                  pl="12px"
                  flexDir="column"
                  borderLeft="3px solid"
                  borderColor="border.seperator"
                  mb="8px"
                >
                  <InputField
                    word={AI_Params_Words["seed-caption"]}
                    value={variation.seed}
                    setValue={(value) => updateVariations(index, "seed", value)}
                    identifier={`variation_seed_${index}`}
                  />
                  <InputField
                    word={AI_Params_Words["strength-caption"]}
                    value={variation.strength}
                    setValue={(value) => updateVariations(index, "strength", value)}
                    identifier={`variation_strength_${index}`}
                  />
                </Flex>
              );
            })}
          </>
        )}
        {renderConfig.useRefine && data.use_refine && (
          <NSlider
            value={data.refine_fidelity}
            min={0}
            max={1}
            step={0.01}
            label={translate(AI_Params_Words["fidelity-caption"], lang)}
            onChange={(fidelity) => {
              updateParamsDataValue("refine_fidelity", fidelity);
            }}
          />
        )}
        {renderConfig.circular && (
          <CircularSwitch
            mb="12px"
            alias={getRandomHash().toString()}
            circular={data.use_circular}
            onChange={(use_circular) => {
              updateParamsDataValue("use_circular", use_circular);
            }}
          />
        )}
        {renderConfig.seed && (
          <InputField
            mb="12px"
            lang={lang}
            identifier="ai-params-seed"
            word={AI_Params_Words["seed-caption"]}
            value={data.seed ?? -1}
            setValue={(seed) => {
              updateParamsDataValue("seed", seed);
            }}
          />
        )}
        {renderConfig.highRes && (
          <HighResSwitch
            value={!!data.highres_info}
            onChange={(highResOpen) => {
              updateParamsDataValue(
                "highres_info",
                highResOpen ? { upscale_factor: 2 } : undefined,
              );
            }}
          />
        )}
      </Box>
      <InfoText mt="12px">
        {translate(Editor_Words["resolution"], lang)}：{w} x {h}px
      </InfoText>

      {aiParamsStore.type && (
        <BizGenerateButton
          mt="48px"
          size={{ w: w ?? 512, h: h ?? 512 }}
          onSubmit={handleGenerate}
          calcPriceProps={type ? GeneratePriceRules[MetaBizTypeMap[type]] : undefined}
          sticky
        >
          {translate(Editor_Words["import-submit"], lang)}
        </BizGenerateButton>
      )}
    </SubWithTitle>
  );
};

export const Params2Img = observer(IParams2Img);
